import { useState, useEffect } from "react";

export const usePWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Device detection
    const userAgent = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(userAgent);
    const safari =
      iOS && /Safari/.test(userAgent) && !/CriOS|FxiOS|OPiOS/i.test(userAgent);

    setIsIOS(iOS);
    setIsSafari(safari);

    // Check if running in standalone mode (launched from home screen)
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    setIsStandalone(standalone);

    // Multi-method installation detection
    const checkInstallation = () => {
      // Method 1: Currently running in standalone mode
      if (standalone) {
        console.log("✅ PWA detected: Running in standalone mode");
        return true;
      }

      // Method 2: Manual tracking via localStorage
      const manualFlag = localStorage.getItem("pwa-installed") === "true";
      if (manualFlag) {
        console.log("✅ PWA detected: Manual flag set");
        return true;
      }

      // Method 3: Check URL parameters (if launched from home screen)
      const urlParams = new URLSearchParams(window.location.search);
      const fromHomeScreen =
        urlParams.get("utm_source") === "homescreen" ||
        urlParams.get("standalone") === "true";
      if (fromHomeScreen) {
        console.log("✅ PWA detected: Launched from home screen");
        return true;
      }

      // Method 4: Check document referrer for PWA launch
      const referrerIsPWA = document.referrer === "" && standalone;
      if (referrerIsPWA) {
        console.log("✅ PWA detected: Empty referrer with standalone");
        return true;
      }

      console.log("❌ PWA not detected or not installed");
      return false;
    };

    setIsInstalled(checkInstallation());

    // Listen for beforeinstallprompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      console.log("🚀 beforeinstallprompt event fired - PWA can be installed");
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);

      // If prompt is available, PWA is definitely not installed
      setIsInstalled(false);
    };

    // Listen for app installation
    const handleAppInstalled = (e) => {
      console.log("✅ PWA successfully installed", e);
      setDeferredPrompt(null);
      setCanInstall(false);
      setIsInstalled(true);
      localStorage.setItem("pwa-installed", "true");
      localStorage.setItem("pwa-install-date", new Date().toISOString());
    };

    // Listen for display mode changes
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const handleDisplayModeChange = (e) => {
      console.log(
        "Display mode changed:",
        e.matches ? "standalone" : "browser"
      );
      setIsStandalone(e.matches);
      setIsInstalled(e.matches);
    };

    // Advanced detection using getInstalledRelatedApps (Chrome 80+)
    const checkRelatedApps = async () => {
      if ("getInstalledRelatedApps" in navigator) {
        try {
          const relatedApps = await navigator.getInstalledRelatedApps();
          console.log("Related apps detected:", relatedApps);

          if (relatedApps.length > 0) {
            setIsInstalled(true);
            localStorage.setItem("pwa-installed", "true");
          }
        } catch (error) {
          console.log("getInstalledRelatedApps not supported:", error);
        }
      }
    };

    // Event listeners
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    if (mediaQuery.addListener) {
      mediaQuery.addListener(handleDisplayModeChange);
    } else {
      mediaQuery.addEventListener("change", handleDisplayModeChange);
    }

    // Advanced detection
    checkRelatedApps();

    // Cleanup
    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);

      if (mediaQuery.removeListener) {
        mediaQuery.removeListener(handleDisplayModeChange);
      } else {
        mediaQuery.removeEventListener("change", handleDisplayModeChange);
      }
    };
  }, []);

  const installPWA = async () => {
    if (isIOS) {
      // iOS: Show installation guide
      console.log("🍎 iOS installation guide triggered");

      // You can trigger your PWAInstallModal here or show instructions
      if (window.showIOSInstallGuide) {
        window.showIOSInstallGuide();
      }

      // Mark as "installed" for UI purposes (user will install manually)
      localStorage.setItem("pwa-install-attempted", "true");
      return false;
    }

    if (!deferredPrompt) {
      console.log("❌ No install prompt available");

      // Show manual installation instructions for Android
      alert(`To install this app:

📱 Chrome: Menu (⋮) → "Add to Home screen"
🔧 Edge: Menu (⋯) → "Apps" → "Install this site as an app"
🦊 Firefox: Menu → "Install"

The app will appear on your home screen!`);

      localStorage.setItem("pwa-install-attempted", "true");
      return false;
    }

    try {
      console.log("📲 Triggering install prompt...");
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      console.log("👤 User choice:", outcome);

      if (outcome === "accepted") {
        setIsInstalled(true);
        localStorage.setItem("pwa-installed", "true");
        localStorage.setItem("pwa-install-date", new Date().toISOString());
        return true;
      } else {
        console.log("❌ Installation declined");
        return false;
      }
    } catch (error) {
      console.error("💥 Installation failed:", error);
      return false;
    } finally {
      setDeferredPrompt(null);
      setCanInstall(false);
    }
  };

  // Additional helper methods
  const getInstallationInfo = () => {
    return {
      canInstall,
      isInstalled,
      isStandalone,
      isIOS,
      isSafari,
      installDate: localStorage.getItem("pwa-install-date"),
      installAttempted:
        localStorage.getItem("pwa-install-attempted") === "true",
    };
  };

  const resetInstallationState = () => {
    localStorage.removeItem("pwa-installed");
    localStorage.removeItem("pwa-install-date");
    localStorage.removeItem("pwa-install-attempted");
    setIsInstalled(false);
  };

  return {
    canInstall,
    isInstalled,
    isStandalone,
    installPWA,
    isIOS,
    isSafari,
    deferredPrompt,
    getInstallationInfo,
    resetInstallationState,
  };
};
